/**
 * setGroups — the app-wide set definition.
 *
 * A "set" is a run of lengths the swimmer did together, segmented by the Apple
 * Watch's OWN `segment` workout events (the watch fires one at each structural
 * boundary), with accidental tiny splits merged. This supersedes the old
 * stroke-continuity `buildSets` (which shredded a mixed-stroke set like the
 * Rockalita into per-stroke fragments). One source of truth: the HR timeline,
 * the ribbon, and the detail sheet must never disagree about what a set is.
 *
 * Segmentation algorithm (validated 8/8 against swim-firehose-2026-06-11.json):
 *   1. Filter events to `type === "segment"`, map each to a [startMs, endMs]
 *      window via `date` + `durationSeconds`, sort by startMs.
 *   2. Merge two adjacent segments when (nextStart - prevEnd) is STRICTLY LESS
 *      than 15000 ms — those are accidental watch splits (June 11: 8/7/12s gaps
 *      merge; real set breaks of >=19s stay separate).
 *   3. Assign each lap to the merged window whose MIDPOINT it falls in; a lap
 *      outside every window attaches to the nearest window by absolute time
 *      distance (defensive — not observed in real data, no orphans on June 11).
 *   4. Roll up meters / durationSeconds / startMs / endMs from member laps.
 *   5. restBeforeMs = (this set startMs) - (previous set endMs); null for set 1.
 *   6. Label deterministically (jargon-free — never BPM, never "SWOLF").
 *
 * DELIBERATELY assigns ALL laps (not only non-excluded ones). The Rockalita's
 * first length (lap 37, a Butterfly) is auto-excluded as a 2SD outlier; skipping
 * it would shear the palindrome to 7 strokes and lose the "The Rockalita" label.
 * The binding spec says "assign laps by time-window containment" with no
 * exclusion filter — a set is a structural unit of the swim, and an outlier
 * length is still part of that set. Downstream aggregates that must ignore
 * excluded laps already filter on `lap.effectiveExcluded` themselves.
 *
 * Pure module (no Svelte runtime), per lib convention.
 */
import type { Lap } from "./laps";
import type { FirehoseWorkoutEvent } from "@contract/types";
import { matchSet } from "./setMatch";
import { SET_DISPLAY } from "./setLibrary";

/** Accidental-split merge threshold. Gaps strictly below this collapse. */
const MERGE_GAP_MS = 15_000;

export interface SetGroup {
  /** 1-based position in the swim. */
  index: number;
  /** Member lengths, in time order. May include effectiveExcluded laps. */
  laps: Lap[];
  startMs: number;
  endMs: number;
  /** Sum of member-lap meters (lap.lengthMeters ?? poolLengthMeters ?? 0). */
  meters: number;
  /** Sum of member-lap durations, seconds. */
  durationSeconds: number;
  /** (this set startMs) - (previous set endMs); null for the first set. */
  restBeforeMs: number | null;
  /** Jargon-free display label ("Warmup", "The Rockalita", "Freestyle Set", …). */
  label: string;
  /** True when the set is a single length (lone-length UI treatment). */
  isLoneLength: boolean;
  /** Swift fast-follow landing slot: the FoundationModels-phrased sentence for
   *  this set, riding the firehose payload when the phone produced one. Optional
   *  and absent today — storyFor (setStory.ts) prefers it verbatim when present
   *  and falls back to the template pools otherwise. See setStory module doc. */
  aiStory?: string | null;
  /**
   * Stable id for cross-swim ledger aggregation.
   * Signature sets: SetDefinition.id ("rockalita", "brokenIm", ...).
   * Everyday sets:  "warmup" | "cooldown" | "freeRepeats" | "straightStroke".
   * Degenerate (null): "wholeSwim" | "mixedSet" | lone single lengths.
   */
  setId: string | null;
}

interface SegWindow {
  startMs: number;
  endMs: number;
}

/** Map a stroke to the word the ribbon shows. "Freestyle" reads as "Free". */
function strokeWord(stroke: string): string {
  return stroke === "Freestyle" ? "Free" : stroke;
}

/** A title derived from the set's SHAPE alone — what it would be called at any
 *  position in the swim. `isKnownSet` marks a signature match (Rockalita, IM,
 *  Killer BS, …): an identity, not a generic description. */
interface ShapeTitle {
  label: string;
  setId: string | null;
  isKnownSet: boolean;
}

/**
 * The title a set earns from its own shape, ignoring where it sits.
 *
 * Order is deliberate so the matcher never fights the live copy:
 *   1. Lone length names its own stroke (single lengths never route to the matcher).
 *   2. The existing all-same copy ("Freestyle set" / "{Stroke} set") — kept ABOVE
 *      the matcher so single-stroke sets keep the copy Scott has already seen
 *      (the matcher's singleStrokeBlock would say "Single-Stroke Block"; we let
 *      the live copy own that case).
 *   3. matchSet(laps) — upgrades the generic "Mixed set" to Rockalita / IM /
 *      Reverse IM / Killer BS / Kick-drill / Stroke-Free where it confidently
 *      matches. null leaves the title as "Mixed set" (STRICT: never guess).
 */
function shapeTitle(laps: Lap[], strokes: string[]): ShapeTitle {
  if (strokes.length === 1) {
    // Lone length: name the stroke itself (spelled out). No ledger id.
    return { label: strokes[0], setId: null, isKnownSet: false };
  }

  const allSame = strokes.every((s) => s === strokes[0]);
  if (allSame) {
    // Freestyle case uses SET_DISPLAY.freeRepeats.displayName so the ribbon label,
    // the recognition card, and the ledger all read the SAME name ("Freestyle set").
    // The non-freestyle case stays stroke-specific ("Backstroke set", …) — that is
    // informative and intentionally NOT routed through the display map.
    return strokes[0] === "Freestyle"
      ? { label: SET_DISPLAY.freeRepeats.displayName, setId: "freeRepeats", isKnownSet: false }
      : { label: `${strokes[0]} set`, setId: "straightStroke", isKnownSet: false };
  }

  const matched = matchSet(laps);
  if (matched !== null) return { label: matched.label, setId: matched.id, isKnownSet: true };

  return { label: "Mixed set", setId: null, isKnownSet: false };
}

/**
 * Deterministic, jargon-free label for one set.
 *
 * Titling order:
 *   1. Whole-swim override — a swim that segments into exactly ONE set is
 *      "The whole swim", never a Warmup (Scott, 2026-06-11).
 *   2. A KNOWN SET trumps the bookend (Scott, 2026-09-09: "I think the bookends
 *      are only bookends if they don't match a set. If it's a known set that
 *      trumps the bookends warmup / cooldown"). Warmup/Cooldown are a POSITION
 *      guess; a signature match is an identity, so it keeps its name wherever it
 *      sits. This is roadmap 29c's "only if it bites in a real swim" — it bit
 *      (May 9: a Rockalita swum last read "Cooldown").
 *   3. First / last position → Warmup / Cooldown, for a set the matcher could
 *      not name. "Known set" is SIGNATURE SETS ONLY (Scott, 2026-09-09): a lone
 *      length, a same-stroke run ("Freestyle set" / "{Stroke} set") and "Mixed
 *      set" are shapes, not identities, so in a bookend slot they still read
 *      Warmup/Cooldown.
 *   4. Otherwise the shape title (lone stroke / all-same copy / Mixed set).
 */
function labelFor(
  laps: Lap[],
  strokes: string[],
  indexInList: number,
  totalSets: number,
): { label: string; setId: string | null } {
  if (totalSets === 1) return { label: "The whole swim", setId: null };

  const shape = shapeTitle(laps, strokes);
  if (shape.isKnownSet) return { label: shape.label, setId: shape.setId };

  if (indexInList === 0) return { label: SET_DISPLAY.warmup.displayName, setId: "warmup" };
  if (indexInList === totalSets - 1) {
    return { label: SET_DISPLAY.cooldown.displayName, setId: "cooldown" };
  }

  return { label: shape.label, setId: shape.setId };
}

export function buildSetGroups(
  events: FirehoseWorkoutEvent[],
  laps: Lap[],
  poolLengthMeters: number | null,
): SetGroup[] {
  if (laps.length === 0) return [];

  // (1) segment windows.
  const windows: SegWindow[] = events
    .filter((e) => e.type === "segment")
    .map((e) => {
      const startMs = new Date(e.date).getTime();
      const durationSeconds = e.durationSeconds ?? 0;
      return { startMs, endMs: startMs + durationSeconds * 1000 };
    })
    .filter((w) => Number.isFinite(w.startMs))
    .sort((a, b) => a.startMs - b.startMs);

  // Fallback: no segments at all → one set covering every lap.
  if (windows.length === 0) {
    return [finalizeSet(laps, 0, 1, null, poolLengthMeters)];
  }

  // (2) merge adjacent windows when the inter-segment gap is < MERGE_GAP_MS.
  const merged: SegWindow[] = [{ ...windows[0] }];
  for (let i = 1; i < windows.length; i++) {
    const prev = merged[merged.length - 1];
    if (windows[i].startMs - prev.endMs < MERGE_GAP_MS) {
      prev.endMs = Math.max(prev.endMs, windows[i].endMs);
    } else {
      merged.push({ ...windows[i] });
    }
  }

  // (3) assign each lap to a window by midpoint containment; orphans attach to
  // the nearest window by absolute time distance.
  const buckets: Lap[][] = merged.map(() => []);
  for (const lap of laps) {
    const mid = lap.startMs + (lap.endMs - lap.startMs) / 2;
    let target = merged.findIndex((w) => mid >= w.startMs && mid < w.endMs);
    if (target === -1) {
      // Nearest window by distance from the lap midpoint to the window span.
      let best = 0;
      let bestDist = Infinity;
      merged.forEach((w, wi) => {
        const dist = mid < w.startMs ? w.startMs - mid : mid >= w.endMs ? mid - w.endMs : 0;
        if (dist < bestDist) {
          bestDist = dist;
          best = wi;
        }
      });
      target = best;
    }
    buckets[target].push(lap);
  }

  // Drop empty buckets (a merged window with no laps) so set indices stay tight.
  const nonEmpty = buckets.filter((b) => b.length > 0);
  const totalSets = nonEmpty.length;

  let prevEndMs: number | null = null;
  const out: SetGroup[] = nonEmpty.map((bucketLaps, i) => {
    const sorted = [...bucketLaps].sort((a, b) => a.startMs - b.startMs);
    const set = finalizeSet(sorted, i, totalSets, prevEndMs, poolLengthMeters);
    prevEndMs = set.endMs;
    return set;
  });

  return mergeAdjacentMosalitas(out);
}

/**
 * Post-pass: a Mosesso set IMMEDIATELY followed by a Reverse Mosesso is one
 * Mosalita swum as two watch segments (rest >= MERGE_GAP_MS between the halves;
 * a shorter rest already merged upstream and matched the 40-length template
 * directly). Merge them into a single SetGroup so both swim shapes render, tally,
 * and ledger identically.
 *
 * Runs AFTER labeling. Since 2026-09-09 a known set trumps the bookend, so a
 * half that lands FIRST or LAST in the swim still carries setId "mosesso" /
 * "reverseMosesso" and DOES merge — the intended outcome of roadmap 29c, which
 * accepted the old position precedence "only if it bites in a real swim".
 */
function mergeAdjacentMosalitas(groups: SetGroup[]): SetGroup[] {
  const out: SetGroup[] = [];
  for (let i = 0; i < groups.length; i++) {
    const a = groups[i];
    const b = groups[i + 1];
    if (a.setId === "mosesso" && b?.setId === "reverseMosesso") {
      const laps = [...a.laps, ...b.laps]; // both halves already time-sorted; a precedes b
      const matched = matchSet(laps);
      if (matched?.id === "mosalita") {
        out.push({
          index: 0, // renumbered below
          laps,
          startMs: a.startMs,
          endMs: b.endMs,
          meters: a.meters + b.meters,
          // Lap-sum convention (finalizeSet): the rest between the halves is
          // excluded, exactly like intra-set pauses today.
          durationSeconds: a.durationSeconds + b.durationSeconds,
          restBeforeMs: a.restBeforeMs,
          label: matched.label,
          isLoneLength: false,
          setId: matched.id,
        });
        i++; // consume b
        continue;
      }
    }
    out.push(a);
  }
  return out.map((g, idx) => ({ ...g, index: idx + 1 }));
}

function finalizeSet(
  sortedLaps: Lap[],
  indexInList: number,
  totalSets: number,
  prevEndMs: number | null,
  poolLengthMeters: number | null,
): SetGroup {
  const startMs = sortedLaps[0].startMs;
  const endMs = sortedLaps[sortedLaps.length - 1].endMs;
  const meters = sortedLaps.reduce((m, l) => m + (l.lengthMeters ?? poolLengthMeters ?? 0), 0);
  const durationSeconds = sortedLaps.reduce((d, l) => d + l.durationSeconds, 0);
  const strokes = sortedLaps.map((l) => l.strokeStyle);
  const titled = labelFor(sortedLaps, strokes, indexInList, totalSets);
  return {
    index: indexInList + 1,
    laps: sortedLaps,
    startMs,
    endMs,
    meters,
    durationSeconds,
    restBeforeMs: prevEndMs === null ? null : startMs - prevEndMs,
    label: titled.label,
    isLoneLength: sortedLaps.length === 1,
    setId: titled.setId,
  };
}

// strokeWord is exported for the ribbon/detail components that render the
// per-stroke word next to a color chip (color reinforces, text carries meaning).
export { strokeWord };
